import { useState } from 'react'
import { Lock, Calendar, Hash } from 'lucide-react'
import { Button } from '@/components/ui/button'

interface AEMCashFormProps {
  onSubmit: (cardData: AEMCashCardData) => Promise<void>
  loading: boolean
  totalAmount: number
}

export interface AEMCashCardData {
  cardNumber: string
  cvc: string
  expirationMonth: string
  expirationYear: string
  cardholderName: string
}

export function AEMCashForm({ onSubmit, loading, totalAmount }: AEMCashFormProps) {
  const [cardData, setCardData] = useState<AEMCashCardData>({
    cardNumber: '',
    cvc: '',
    expirationMonth: '',
    expirationYear: '',
    cardholderName: ''
  })

  const [errors, setErrors] = useState<Partial<Record<keyof AEMCashCardData, string>>>({})

  const formatCardNumber = (value: string) => {
    const cleaned = value.replace(/\s/g, '')
    const groups = cleaned.match(/.{1,4}/g)
    return groups ? groups.join(' ') : cleaned
  }

  const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\s/g, '')
    if (/^\d*$/.test(value) && value.length <= 16) {
      setCardData(prev => ({ ...prev, cardNumber: value }))
      setErrors(prev => ({ ...prev, cardNumber: undefined }))
    }
  }

  const handleCVCChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    if (/^\d*$/.test(value) && value.length <= 4) {
      setCardData(prev => ({ ...prev, cvc: value }))
      setErrors(prev => ({ ...prev, cvc: undefined }))
    }
  }

  const handleMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setCardData(prev => ({ ...prev, expirationMonth: e.target.value }))
    setErrors(prev => ({ ...prev, expirationMonth: undefined }))
  }

  const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setCardData(prev => ({ ...prev, expirationYear: e.target.value }))
    setErrors(prev => ({ ...prev, expirationYear: undefined }))
  }

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCardData(prev => ({ ...prev, cardholderName: e.target.value }))
    setErrors(prev => ({ ...prev, cardholderName: undefined }))
  }

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof AEMCashCardData, string>> = {}

    if (!cardData.cardNumber || cardData.cardNumber.length < 13) {
      newErrors.cardNumber = 'Numéro de carte invalide'
    }

    if (!cardData.cvc || cardData.cvc.length < 3) {
      newErrors.cvc = 'CVC invalide'
    }

    if (!cardData.expirationMonth) {
      newErrors.expirationMonth = 'Mois requis'
    }

    if (!cardData.expirationYear) {
      newErrors.expirationYear = 'Année requise'
    }

    if (!cardData.cardholderName || cardData.cardholderName.length < 3) {
      newErrors.cardholderName = 'Nom du titulaire requis'
    }

    const currentYear = new Date().getFullYear()
    const currentMonth = new Date().getMonth() + 1
    const expYear = parseInt(cardData.expirationYear)
    const expMonth = parseInt(cardData.expirationMonth)

    if (expYear < currentYear || (expYear === currentYear && expMonth < currentMonth)) {
      newErrors.expirationMonth = 'Date d\'expiration invalide'
      newErrors.expirationYear = 'Date d\'expiration invalide'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (validateForm()) {
      await onSubmit(cardData)
    }
  }

  const currentYear = new Date().getFullYear()
  const years = Array.from({ length: 15 }, (_, i) => currentYear + i)
  const months = Array.from({ length: 12 }, (_, i) => (i + 1).toString().padStart(2, '0'))

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-gradient-to-br from-blue-600 to-blue-800 rounded-xl p-6 text-white shadow-xl">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-3">
            <img src="/aem_cash.png" alt="AEM Cash" className="h-8 w-auto" />
            <span className="text-xl font-bold">AEM Cash</span>
          </div>
          <Lock className="h-5 w-5 opacity-75" />
        </div>

        <div className="space-y-4">
          <div className="text-2xl font-mono tracking-wider">
            {formatCardNumber(cardData.cardNumber) || '•••• •••• •••• ••••'}
          </div>

          <div className="flex justify-between items-end">
            <div>
              <div className="text-xs opacity-75 mb-1">Titulaire</div>
              <div className="font-medium uppercase">
                {cardData.cardholderName || 'NOM PRENOM'}
              </div>
            </div>
            <div>
              <div className="text-xs opacity-75 mb-1">Expire</div>
              <div className="font-medium font-mono">
                {cardData.expirationMonth && cardData.expirationYear
                  ? `${cardData.expirationMonth}/${cardData.expirationYear.slice(-2)}`
                  : '••/••'}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Nom du titulaire de la carte
        </label>
        <input
          type="text"
          value={cardData.cardholderName}
          onChange={handleNameChange}
          placeholder="JEAN DUPONT"
          className={`block w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 ${
            errors.cardholderName
              ? 'border-red-300 focus:ring-red-500'
              : 'border-gray-300 focus:ring-blue-500'
          }`}
          required
        />
        {errors.cardholderName && (
          <p className="mt-1 text-sm text-red-600">{errors.cardholderName}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          <div className="flex items-center">
            <Hash className="h-4 w-4 mr-1" />
            Numéro de carte
          </div>
        </label>
        <input
          type="text"
          value={formatCardNumber(cardData.cardNumber)}
          onChange={handleCardNumberChange}
          placeholder="1234 5678 9012 3456"
          className={`block w-full px-4 py-3 border rounded-lg font-mono text-lg focus:outline-none focus:ring-2 ${
            errors.cardNumber
              ? 'border-red-300 focus:ring-red-500'
              : 'border-gray-300 focus:ring-blue-500'
          }`}
          required
        />
        {errors.cardNumber && (
          <p className="mt-1 text-sm text-red-600">{errors.cardNumber}</p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <div className="flex items-center">
              <Calendar className="h-4 w-4 mr-1" />
              Date d'expiration
            </div>
          </label>
          <div className="grid grid-cols-2 gap-2">
            <select
              value={cardData.expirationMonth}
              onChange={handleMonthChange}
              className={`block w-full px-3 py-3 border rounded-lg focus:outline-none focus:ring-2 ${
                errors.expirationMonth
                  ? 'border-red-300 focus:ring-red-500'
                  : 'border-gray-300 focus:ring-blue-500'
              }`}
              required
            >
              <option value="">Mois</option>
              {months.map(month => (
                <option key={month} value={month}>
                  {month}
                </option>
              ))}
            </select>
            <select
              value={cardData.expirationYear}
              onChange={handleYearChange}
              className={`block w-full px-3 py-3 border rounded-lg focus:outline-none focus:ring-2 ${
                errors.expirationYear
                  ? 'border-red-300 focus:ring-red-500'
                  : 'border-gray-300 focus:ring-blue-500'
              }`}
              required
            >
              <option value="">Année</option>
              {years.map(year => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
          {(errors.expirationMonth || errors.expirationYear) && (
            <p className="mt-1 text-sm text-red-600">
              {errors.expirationMonth || errors.expirationYear}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <div className="flex items-center">
              <Lock className="h-4 w-4 mr-1" />
              CVC
            </div>
          </label>
          <input
            type="text"
            value={cardData.cvc}
            onChange={handleCVCChange}
            placeholder="123"
            maxLength={4}
            className={`block w-full px-4 py-3 border rounded-lg font-mono text-lg focus:outline-none focus:ring-2 ${
              errors.cvc
                ? 'border-red-300 focus:ring-red-500'
                : 'border-gray-300 focus:ring-blue-500'
            }`}
            required
          />
          {errors.cvc && (
            <p className="mt-1 text-sm text-red-600">{errors.cvc}</p>
          )}
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <Lock className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="ml-3">
            <h4 className="text-sm font-medium text-blue-900">Paiement sécurisé</h4>
            <p className="mt-1 text-sm text-blue-700">
              Vos informations de carte sont cryptées et sécurisées par AEM Cash.
              Nous ne stockons jamais vos données de paiement.
            </p>
          </div>
        </div>
      </div>

      <div className="border-t pt-4">
        <div className="flex justify-between items-center mb-4">
          <span className="text-lg font-medium text-gray-900">Montant total</span>
          <span className="text-2xl font-bold text-blue-600">
            ${totalAmount.toFixed(2)}
          </span>
        </div>
      </div>

      <Button
        type="submit"
        variant="primary"
        className="w-full py-4 text-lg"
        disabled={loading}
      >
        {loading ? (
          <div className="flex items-center justify-center">
            <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
            Traitement en cours...
          </div>
        ) : (
          `Payer $${totalAmount.toFixed(2)}`
        )}
      </Button>

      <p className="text-xs text-center text-gray-500">
        En confirmant ce paiement, vous acceptez les conditions d'utilisation d'AEM Cash
      </p>
    </form>
  )
}
